/**
 * Postaja, kot jo vidi aplikacija (T2 §2).
 *
 * Tip nastane v fazi 1, ker ga `AudioPort.load()` potrebuje. Shramba,
 * verzioniranje in migracije pridejo v fazi 2 — tip je že takrat celoten,
 * da se seed in izvoz ne pišeta dvakrat.
 */

export type StationSource = 'radio-browser' | 'webradiodb' | 'manual' | 'seed';

export type MetaCapability =
  | { kind: 'azuracast-sse'; url: string }
  /**
   * `viaProxy` pomeni, da je Icecast dosegljiv **samo** prek `/api/meta`.
   *
   * Direktni zahtevek na postajo brez CORS brskalnik zavrne, in ta zavrnitev
   * je nujen del zaznave — ne da se je preskočiti, ne da se je utišati. Brez
   * tega zapisa jo je vsaka nova seja ponovila: en zavrnjen zahtevek in ena
   * rdeča vrstica v konzoli na vsako menjavo postaje.
   *
   * Polje je neobvezno: zapis brez njega je iz starejše različice in pomeni
   * »ne vem«, torej se direktna pot poskusi. Migracije zato ne rabi.
   */
  | { kind: 'icecast-json'; url: string; viaProxy?: boolean }
  | { kind: 'hls-cue' }
  | { kind: 'icy-proxy' }
  | { kind: 'none' };

export interface Station {
  /** Naš uuid, ne uuid vira — vir ga lahko spremeni ali izgubi (T2 §2). */
  id: string;
  name: string;
  /** Tok, kot ga je dal vir. */
  url: string;
  /** Po razrešitvi preusmeritev ali po nadgradnji sheme (T2 §11). */
  urlResolved?: string;
  homepage?: string;
  favicon?: string;
  country?: string;
  countryCode?: string;
  tags: string[];
  codec?: string;
  /** kbps; 0 ali manjka = neznano. Kakovost NI status (CLAUDE.md §signal ≠ status). */
  bitrate?: number;
  isHls: boolean;
  /** Izpeljano iz `urlResolved ?? url` — glej `isHttpOnly()`. */
  isHttps: boolean;
  favorite: boolean;
  order: number;
  addedAt: number;
  lastPlayedAt?: number;
  playCount: number;
  source: StationSource;
  sourceUuid?: string;
  metaCapability?: MetaCapability;
  /**
   * Kdaj je bila sposobnost ugotovljena (epoch ms).
   *
   * `metaCapability` je predpomnilnik odločitve, ne dejstvo (T2 §2): postaja
   * lahko zamenja ponudnika. Polje je neobvezno, zato zanj ni migracije —
   * zapis brez njega velja za veljaven, sicer bi nadgradnja naenkrat pognala
   * kaskado čez cel seznam.
   */
  metaCheckedAt?: number;
}

/**
 * Mehka veljavnost zapisa `metaCapability` (T2 §2).
 *
 * Zapis je predpomnilnik odločitve, ne dejstvo — postaja lahko zamenja
 * ponudnika metapodatkov, ne da bi se karkoli drugega spremenilo.
 */
export const CAPABILITY_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * 🔴 `none` velja krajše od ostalih izidov.
 *
 * Vsak drug zapis se ob zmoti popravi sam: poll teče, neuspehi se štejejo in
 * po treh se kaskada požene znova. `none` te poti nima — pomeni »ne poizveduj«,
 * torej ni pollov, ni neuspehov in ni popravka. Napačen `none` je zato edini
 * zapis, ki utihne za ves svoj rok, in rok mora biti temu primeren.
 */
export const NONE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * Ali se sme shranjeni izid kaskade uporabiti brez ponovnega preverjanja.
 *
 * Zapis brez `metaCheckedAt` je iz starejše različice in velja za veljavnega:
 * sicer bi nadgradnja ob prvem zagonu pognala kaskado čez cel seznam naenkrat.
 */
export function capabilityIsFresh(station: Station, now: number): boolean {
  const capability = station.metaCapability;
  if (capability === undefined) return false;
  if (station.metaCheckedAt === undefined) return true;
  const maxAge = capability.kind === 'none' ? NONE_MAX_AGE_MS : CAPABILITY_MAX_AGE_MS;
  return now - station.metaCheckedAt < maxAge;
}

/** URL, ki se dejansko predvaja: razrešeni, če obstaja. */
export function effectiveUrl(station: Station): string {
  return station.urlResolved ?? station.url;
}

/**
 * Ali postaja oddaja samo prek `http:`.
 *
 * To ni kozmetika: s HTTPS strani brskalnik tak tok blokira kot mešano
 * vsebino, zato mora iti prek proxyja (T4 §3). Pove se vnaprej v seznamu,
 * ne šele ob poskusu predvajanja (T2 §6).
 */
export function isHttpOnly(station: Station): boolean {
  return effectiveUrl(station).startsWith('http:');
}

/** Iskanje po id z jasnim rezultatom namesto `undefined` na klicnem mestu. */
export function findStation(stations: readonly Station[], id: string | null): Station | null {
  if (id === null) return null;
  return stations.find((s) => s.id === id) ?? null;
}

/**
 * Sosed v seznamu, ciklično. Transport prejšnja/naslednja se v radiu
 * obnaša kot preklop postaje, ne kot skok po posnetku.
 */
export function neighbourStation(
  stations: readonly Station[],
  id: string | null,
  step: 1 | -1,
): Station | null {
  if (stations.length === 0) return null;
  const index = stations.findIndex((s) => s.id === id);
  if (index === -1) {
    /**
     * 🔴 Tekoča postaja ni v seznamu — **smer mora ostati**.
     *
     * Zgodi se pri predposluhu iz Iskanja (namenoma ni v `library.visible()`,
     * P2 §2.4) in po brisanju tekoče postaje (viseč `stationId`). Prej je oba
     * primera vrnil `stations[0]` **ne glede na smer**, zato sta na zaklenjenem
     * zaslonu ⏮ in ⏭ postala isti gumb: oba skočita na prvo vidno postajo.
     * ⏭ pelje na začetek, ⏮ na konec — kot da bi bili tik ob robu.
     */
    return step === 1 ? (stations[0] ?? null) : (stations[stations.length - 1] ?? null);
  }

  const next = (index + step + stations.length) % stations.length;
  return stations[next] ?? null;
}
